import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  ErrorOutline,
  Groups,
  MailOutline,
  Preview,
  People,
  Refresh,
  RestartAlt,
  Send,
  WifiTethering,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../context/AlertContext';
import { useConfirmation } from '../../context/ConfirmationContext';
import {
  IMailSenderGroup,
  IMailSenderUser,
  useMailSenderInfo,
  useMailSettings,
  useResetMailSettings,
  useSendTestMail,
  useTestMailConnection,
  useUpdateMailSettings,
} from '../../hooks/reactQuerys/MailSettingQueries';

/**
 * #1256 — E-Mail-Grundeinstellungen (Einstellungen → Grundeinstellungen).
 * Aufbau wie „Termine": Tabs. Tab 1 = E-Mail-Server (SMTP) inkl. Verbindungstest,
 * Tab 2 = Absender für E-Mails aus Vorgängen und Vollzug.
 */
const backendError = (err: unknown): string | undefined =>
  (err as { body?: { error?: string } })?.body?.error;

/** Feld/Block über die volle Breite eines zweispaltigen Formular-Grids. */
const FULL_ROW = { gridColumn: { xs: 'auto', sm: '1 / -1' } };

const statusChip = (status: string) => {
  if (status === 'ok')
    return <Chip size='small' color='success' label='Verbunden' />;
  if (status === 'fehler')
    return <Chip size='small' color='error' label='Fehler' />;
  return <Chip size='small' color='default' label='Ungeprüft' />;
};

/**
 * Ein Benutzer in der Signatur-Übersicht: links Ampel + Name/Details, rechts
 * der Status. Klick öffnet den Benutzer direkt in der Benutzerverwaltung.
 */
type ISenderRowProps = {
  name: string;
  details: string;
  hasSignature: boolean;
  hasEmail: boolean;
  tooltip: string;
  onOpen: () => void;
};

const SenderRow: React.FC<ISenderRowProps> = ({
  name,
  details,
  hasSignature,
  hasEmail,
  tooltip,
  onOpen,
}) => (
  <ListItem disableGutters disablePadding>
    <Tooltip title={tooltip}>
      <ListItemButton onClick={onOpen} sx={{ borderRadius: 1, py: 1 }}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          {!hasEmail ? (
            <ErrorOutline color='error' fontSize='small' />
          ) : !hasSignature ? (
            <ErrorOutline color='warning' fontSize='small' />
          ) : (
            <CheckCircle color='success' fontSize='small' />
          )}
        </ListItemIcon>
        <ListItemText
          primary={name}
          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          secondary={details}
          secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
        />
        <Stack
          direction='row'
          spacing={1}
          sx={{ ml: 2, flexShrink: 0 }}
          alignItems='center'
        >
          {/* Ohne E-Mail-Adresse kann dieser Absender gar nicht auftreten —
              deshalb als eigener Status neben der Signatur. */}
          {!hasEmail && (
            <Chip
              size='small'
              variant='outlined'
              color='error'
              label='Keine E-Mail-Adresse'
            />
          )}
          <Chip
            size='small'
            variant='outlined'
            color={hasSignature ? 'success' : 'warning'}
            label={hasSignature ? 'Signatur vorhanden' : 'Keine Signatur'}
          />
        </Stack>
      </ListItemButton>
    </Tooltip>
  </ListItem>
);

/** Ein Benutzer in der Signatur-Übersicht. */
type ISenderUserRowProps = {
  user: IMailSenderUser;
  onOpenUser: (idUser: number) => void;
};

const SenderUserRow: React.FC<ISenderUserRowProps> = ({ user, onOpenUser }) => (
  <SenderRow
    name={user.Name}
    // Rolle/Gruppen bleiben Fließtext — Chips nur für den Signatur-Status,
    // damit die Zeile nicht aus lauter bunten Kacheln besteht.
    details={[
      user.hasEmail ? user.Email : null,
      user.isNotary ? 'Notar' : null,
      ...user.Groups,
    ]
      .filter(Boolean)
      .join(' · ')}
    hasSignature={user.hasSignature}
    hasEmail={user.hasEmail}
    tooltip='Benutzer bearbeiten'
    onOpen={() => onOpenUser(user.idUser)}
  />
);

/** Eine Mitarbeitergruppe (Gruppenpostfach) in der Signatur-Übersicht. */
type ISenderGroupRowProps = {
  group: IMailSenderGroup;
  onOpenGroup: (idUserGroup: number) => void;
};

const SenderGroupRow: React.FC<ISenderGroupRowProps> = ({
  group,
  onOpenGroup,
}) => (
  <SenderRow
    name={group.Name}
    details={[
      group.hasEmail ? group.Email : null,
      `${group.MemberCount} Mitglied${group.MemberCount === 1 ? '' : 'er'}`,
    ]
      .filter(Boolean)
      .join(' · ')}
    hasSignature={group.hasSignature}
    hasEmail={group.hasEmail}
    tooltip='Benutzergruppe bearbeiten'
    onOpen={() => onOpenGroup(group.idUserGroup)}
  />
);

export const MailSettingsView: React.FC = () => {
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const navigate = useNavigate();
  const { mailSettings, isLoadingMailSettings } = useMailSettings();
  const { updateMailSettings, isUpdatingMailSettings } =
    useUpdateMailSettings();
  const { testMailConnection, isTestingMailConnection } =
    useTestMailConnection();
  const { sendTestMail, isSendingTestMail } = useSendTestMail();
  const { resetMailSettings, isResettingMailSettings } = useResetMailSettings();

  const [tab, setTab] = useState(0);
  const [server, setServer] = useState('');
  const [port, setPort] = useState('');
  const [encryption, setEncryption] = useState('starttls');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [recipient, setRecipient] = useState('');
  const [senderPolicy, setSenderPolicy] = useState('system');
  const [senderCustomAddress, setSenderCustomAddress] = useState('');
  const [senderCustomName, setSenderCustomName] = useState('');
  const [senderRewriteMode, setSenderRewriteMode] = useState('auto');
  // Erläuterung + Signatur-Prüfung folgen der aktuell gewählten (ggf. noch
  // ungespeicherten) Absender-Regel.
  const { mailSenderInfo, isLoadingMailSenderInfo, refetchMailSenderInfo } =
    useMailSenderInfo(senderPolicy);

  /** Öffnet den Benutzer direkt in der Benutzerverwaltung (Bearbeiten-Dialog). */
  const openUser = (idUser: number) =>
    navigate('/admin/user', { state: { idUser } });

  /** Öffnet die Benutzergruppe direkt im Bearbeiten-Dialog. */
  const openUserGroup = (idUserGroup: number) =>
    navigate('/admin/groups', { state: { idUserGroup } });

  useEffect(() => {
    if (mailSettings) {
      setServer(mailSettings.SmtpServer ?? '');
      setPort(mailSettings.SmtpPort ?? '');
      setEncryption(mailSettings.Encryption ?? 'starttls');
      setUsername(mailSettings.Username ?? '');
      setFromAddress(mailSettings.FromAddress ?? '');
      setFromName(mailSettings.FromName ?? '');
      setSenderPolicy(mailSettings.SenderPolicy ?? 'system');
      setSenderCustomAddress(mailSettings.SenderCustomAddress ?? '');
      setSenderCustomName(mailSettings.SenderCustomName ?? '');
      setSenderRewriteMode(mailSettings.SenderRewriteMode ?? 'auto');
      setPassword('');
    }
  }, [mailSettings]);

  const formBody = () => ({
    SmtpServer: server.trim(),
    SmtpPort: port.trim(),
    Encryption: encryption,
    Username: username.trim(),
    // leeres Passwortfeld => bestehendes Passwort bleibt erhalten
    Password: password ? password : undefined,
    FromAddress: fromAddress.trim(),
    FromName: fromName.trim(),
    SenderPolicy: senderPolicy,
    SenderCustomAddress: senderCustomAddress.trim(),
    SenderCustomName: senderCustomName.trim(),
    SenderRewriteMode: senderRewriteMode,
  });

  const handleSave = async (successText: string) => {
    try {
      await updateMailSettings(formBody());
      showAlert({ text: successText, severity: 'success' });
    } catch (e) {
      showAlert({
        text: backendError(e) ?? 'Speichern fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const handleTest = async () => {
    try {
      const res = (await testMailConnection(formBody())) as {
        status?: string;
        error?: string;
      };
      showAlert({
        text:
          res.status === 'ok'
            ? 'Verbindung erfolgreich.'
            : `Verbindung fehlgeschlagen: ${res.error ?? ''}`,
        severity: res.status === 'ok' ? 'success' : 'error',
      });
    } catch (e) {
      showAlert({
        text: backendError(e) ?? 'Test fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const handleSendTest = async () => {
    if (!recipient.trim()) {
      showAlert({
        text: 'Bitte Empfänger für die Test-E-Mail angeben.',
        severity: 'warning',
      });
      return;
    }
    try {
      const res = (await sendTestMail({
        ...formBody(),
        recipient: recipient.trim(),
      })) as {
        status?: string;
        error?: string;
      };
      showAlert({
        text:
          res.status === 'ok'
            ? `Test-E-Mail an ${recipient.trim()} versendet.`
            : `Versand fehlgeschlagen: ${res.error ?? ''}`,
        severity: res.status === 'ok' ? 'success' : 'error',
      });
    } catch (e) {
      showAlert({
        text: backendError(e) ?? 'Versand fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const handleReset = async () => {
    // Sicherheitsabfrage: das Zurücksetzen verwirft die komplette lokale
    // SMTP-Konfiguration (inkl. Passwort) und lässt sie wieder vom
    // License-Server überschreiben.
    const confirmed = await showConfirmation({
      title: 'SMTP-Einstellungen zurücksetzen?',
      message: (
        <>
          Die lokale SMTP-Konfiguration wird verworfen und durch die
          Standardkonfiguration des License-Servers ersetzt — einschließlich
          Server, Port, Benutzername und <strong>Passwort</strong>. Künftige
          Änderungen vom License-Server werden dann wieder übernommen.
          <br />
          <br />
          Die Absender-Einstellungen (Reiter „Absender") bleiben unverändert.
        </>
      ),
      confirmButtonText: 'Zurücksetzen',
      abortButtonText: 'Abbrechen',
    });
    if (!confirmed) {
      return;
    }
    try {
      await resetMailSettings();
      showAlert({
        text: 'Auf License-Server-Konfiguration zurückgesetzt.',
        severity: 'success',
      });
    } catch (e) {
      showAlert({
        text: backendError(e) ?? 'Zurücksetzen fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  if (isLoadingMailSettings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const busy =
    isUpdatingMailSettings ||
    isTestingMailConnection ||
    isSendingTestMail ||
    isResettingMailSettings;

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={tab}
        onChange={(_e, value: number) => setTab(value)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label='E-Mail-Server (SMTP)' />
        <Tab label='Absender (Vorgänge & Vollzug)' />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Alert
            severity={mailSettings?.isLocallyConfigured ? 'success' : 'info'}
            sx={{ mb: 2 }}
          >
            {mailSettings?.isLocallyConfigured
              ? 'Diese Einstellungen wurden lokal angepasst und werden nicht mehr vom License-Server überschrieben.'
              : 'Diese Einstellungen stammen aktuell vom License-Server (Standardkonfiguration) und werden bis zur ersten lokalen Änderung von dort aktualisiert.'}
          </Alert>

          <Box
            sx={{
              display: 'grid',
              // Erst auf wirklich breiten Bildschirmen zweispaltig — der
              // Einstellungsbereich belegt nur 9 von 12 Spalten.

              gap: 2,
              alignItems: 'start',
            }}
          >
            <Paper variant='outlined' sx={{ p: 3 }}>
              <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
                Serverdaten
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                <TextField
                  label='SMTP-Server'
                  value={server}
                  onChange={e => setServer(e.target.value)}
                  fullWidth
                  size='small'
                  sx={FULL_ROW}
                />
                <TextField
                  label='Port'
                  value={port}
                  onChange={e => setPort(e.target.value)}
                  fullWidth
                  size='small'
                />
                <TextField
                  select
                  label='Verschlüsselung'
                  value={encryption}
                  onChange={e => setEncryption(e.target.value)}
                  fullWidth
                  size='small'
                >
                  <MenuItem value='none'>Keine</MenuItem>
                  <MenuItem value='starttls'>STARTTLS</MenuItem>
                  <MenuItem value='ssl'>SSL/TLS</MenuItem>
                </TextField>
                <TextField
                  label='Benutzername'
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  fullWidth
                  size='small'
                />
                <TextField
                  label='Passwort'
                  type='password'
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={
                    mailSettings?.hasPassword ? 'unverändert lassen' : ''
                  }
                  helperText={
                    mailSettings?.hasPassword
                      ? 'Leer lassen, um das gespeicherte Passwort beizubehalten.'
                      : undefined
                  }
                  fullWidth
                  size='small'
                />
                <TextField
                  label='Standard-Absenderadresse'
                  value={fromAddress}
                  onChange={e => setFromAddress(e.target.value)}
                  fullWidth
                  size='small'
                />
                <TextField
                  label='Standard-Absendername'
                  value={fromName}
                  onChange={e => setFromName(e.target.value)}
                  fullWidth
                  size='small'
                />
                <Box
                  sx={{
                    ...FULL_ROW,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                  }}
                >
                  <Button
                    variant='outlined'
                    color='inherit'
                    startIcon={<RestartAlt />}
                    onClick={handleReset}
                    disabled={busy || !mailSettings?.isLocallyConfigured}
                  >
                    Zurücksetzen
                  </Button>
                  <Button
                    variant='contained'
                    onClick={() =>
                      handleSave('SMTP-Einstellungen gespeichert.')
                    }
                    disabled={busy}
                  >
                    {isUpdatingMailSettings ? (
                      <CircularProgress size={20} />
                    ) : (
                      'Speichern'
                    )}
                  </Button>
                </Box>
              </Box>
            </Paper>

            <Paper variant='outlined' sx={{ p: 3 }}>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ mb: 2 }}
              >
                <Typography
                  variant='subtitle1'
                  sx={{ fontWeight: 600, flexGrow: 1 }}
                >
                  Verbindung & Test
                </Typography>
                {statusChip(mailSettings?.ConnectionStatus ?? 'ungeprueft')}
              </Stack>
              {mailSettings?.ConnectionStatus === 'fehler' &&
                mailSettings?.LastError && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                    {mailSettings.LastError}
                  </Alert>
                )}
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Der Test verwendet die oben eingetragenen Werte (auch
                ungespeichert). Bleibt das Passwortfeld leer, wird das
                gespeicherte Passwort genutzt.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Button
                  variant='outlined'
                  startIcon={<WifiTethering />}
                  onClick={handleTest}
                  disabled={busy}
                  sx={{ flex: '0 0 auto' }}
                >
                  {isTestingMailConnection ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Verbindung testen'
                  )}
                </Button>
                <TextField
                  label='Test-E-Mail an'
                  value={recipient}
                  onChange={e => setRecipient(e.target.value)}
                  size='small'
                  sx={{ flex: '1 1 220px' }}
                />
                <Button
                  variant='outlined'
                  startIcon={<Send />}
                  onClick={handleSendTest}
                  disabled={busy}
                  sx={{ flex: '0 0 auto' }}
                >
                  {isSendingTestMail ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Test-E-Mail senden'
                  )}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}

      {tab === 1 && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Paper variant='outlined' sx={{ p: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Absender für E-Mails aus Vorgängen und Vollzug
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Legt fest, welcher Absender bei E-Mails aus Vorgängen und dem
              Vollzug verwendet wird. Der SMTP-Versand erfolgt weiterhin über
              die im Reiter „E-Mail-Server (SMTP)“ konfigurierten Serverdaten.
            </Typography>
            <Stack spacing={2}>
              <TextField
                select
                label='Absender'
                value={senderPolicy}
                onChange={e => setSenderPolicy(e.target.value)}
                fullWidth
                size='small'
              >
                <MenuItem value='system'>System (Standard-Absender)</MenuItem>
                <MenuItem value='notary'>
                  Zugeordneter Notar des Vorgangs
                </MenuItem>
                <MenuItem value='employee'>
                  Zugeordneter Mitarbeiter des Vorgangs
                </MenuItem>
                <MenuItem value='saving_user'>
                  Speichernder Benutzer des Vorgangs
                </MenuItem>
                <MenuItem value='custom'>Sonstiger Absender</MenuItem>
              </TextField>
              {/* Zuerst das Ergebnis, dann die Regel: die QS hat zurecht
                  angemerkt, dass man auf einen Blick sehen muss, unter welcher
                  Adresse tatsaechlich versendet wird. Das Beispiel rechnet die
                  Kette mit den echten Benutzern und Gruppen durch. */}
              {mailSenderInfo?.Example && (
                <Paper
                  variant='outlined'
                  sx={{
                    p: 2,
                    borderWidth:
                      mailSenderInfo.Example.Severity === 'warning' ? 2 : 1,
                    borderColor:
                      mailSenderInfo.Example.Severity === 'warning'
                        ? 'warning.main'
                        : 'divider',
                    backgroundColor: 'background.default',
                  }}
                >
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{ mb: 1 }}
                  >
                    <Preview fontSize='small' color='action' />
                    <Typography
                      variant='overline'
                      color='text.secondary'
                      sx={{ letterSpacing: 1 }}
                    >
                      Was der Mandant sieht
                    </Typography>
                    {mailSenderInfo.Example.Severity === 'warning' && (
                      <Chip
                        size='small'
                        color='warning'
                        label='so nicht wie gewünscht'
                      />
                    )}
                  </Stack>

                  <Typography variant='body2' color='text.secondary'>
                    {mailSenderInfo.Example.Case}
                  </Typography>

                  {mailSenderInfo.Example.FromAddress && (
                    <Typography variant='body1' sx={{ mt: 1, mb: 0.5 }}>
                      Absender:{' '}
                      <Box component='span' sx={{ fontWeight: 600 }}>
                        {mailSenderInfo.Example.FromName}
                      </Box>{' '}
                      <Box
                        component='span'
                        sx={{
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                        }}
                      >
                        &lt;{mailSenderInfo.Example.FromAddress}&gt;
                      </Box>
                    </Typography>
                  )}

                  {mailSenderInfo.Example.ResultNote && (
                    <Typography variant='body2'>
                      {mailSenderInfo.Example.ResultNote}
                    </Typography>
                  )}
                  {mailSenderInfo.Example.Signature && (
                    <Typography variant='body2' sx={{ mt: 0.5 }}>
                      {mailSenderInfo.Example.Signature}
                    </Typography>
                  )}
                  {mailSenderInfo.Example.Warning && (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 1 }}
                    >
                      {mailSenderInfo.Example.Warning}
                    </Typography>
                  )}
                </Paper>
              )}
              {mailSenderInfo && (
                <Alert
                  severity='info'
                  icon={<MailOutline fontSize='inherit' />}
                >
                  <AlertTitle sx={{ mb: 0.5 }}>
                    {mailSenderInfo.Label}
                  </AlertTitle>
                  <Typography variant='body2'>
                    {mailSenderInfo.Description}
                  </Typography>
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    {mailSenderInfo.SignatureHint}
                  </Typography>
                  {mailSenderInfo.InternalMailHint && (
                    <Typography variant='body2' sx={{ mt: 1 }}>
                      {mailSenderInfo.InternalMailHint}
                    </Typography>
                  )}
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mt: 1 }}
                  >
                    {mailSenderInfo.TransportHint}
                  </Typography>
                </Alert>
              )}
              {senderPolicy === 'custom' && (
                <Stack direction='row' spacing={2}>
                  <TextField
                    label='Absenderadresse'
                    value={senderCustomAddress}
                    onChange={e => setSenderCustomAddress(e.target.value)}
                    fullWidth
                    size='small'
                  />
                  <TextField
                    label='Absendername'
                    value={senderCustomName}
                    onChange={e => setSenderCustomName(e.target.value)}
                    fullWidth
                    size='small'
                  />
                </Stack>
              )}
              {senderPolicy !== 'system' && (
                <>
                  <TextField
                    select
                    label='Umgang mit der Absenderadresse'
                    value={senderRewriteMode}
                    onChange={e => setSenderRewriteMode(e.target.value)}
                    fullWidth
                    size='small'
                    helperText={mailSenderInfo?.RewriteHint ?? undefined}
                  >
                    <MenuItem value='auto'>Automatisch (empfohlen)</MenuItem>
                    <MenuItem value='from'>
                      Absenderadresse ersetzen („Senden als")
                    </MenuItem>
                    <MenuItem value='replyto'>
                      Nur Absendername und Antwort-an
                    </MenuItem>
                  </TextField>
                  {senderRewriteMode === 'replyto' && (
                    <Alert severity='warning'>
                      E-Mails werden weiterhin über die Adresse des SMTP-Kontos
                      versendet — sichtbar ist der Name des Absenders, Antworten
                      gehen an dessen Adresse. Viele Anbieter (z. B. Microsoft
                      365) weisen E-Mails mit fremder Absenderadresse sonst
                      zurück („Senden als" nicht erlaubt).
                    </Alert>
                  )}
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant='contained'
                  onClick={() =>
                    handleSave('Absender-Einstellung gespeichert.')
                  }
                  disabled={busy}
                >
                  {isUpdatingMailSettings ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Speichern'
                  )}
                </Button>
              </Box>
            </Stack>
          </Paper>

          {mailSenderInfo?.checksSignatures && (
            <Paper variant='outlined' sx={{ p: 3 }}>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ mb: 1 }}
              >
                <Typography
                  variant='subtitle1'
                  sx={{ fontWeight: 600, flexGrow: 1 }}
                >
                  Mögliche Absender: E-Mail-Adresse & Signatur
                </Typography>
                <Tooltip title='Prüfung erneut ausführen'>
                  <span>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<Refresh />}
                      onClick={() => refetchMailSenderInfo()}
                      disabled={isLoadingMailSenderInfo}
                    >
                      Aktualisieren
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Geprüft werden: {mailSenderInfo.ScopeLabel}. Gesperrte Benutzer
                werden nicht berücksichtigt
                {mailSenderInfo.Counts.blockedSkipped > 0
                  ? ` (${mailSenderInfo.Counts.blockedSkipped} übersprungen)`
                  : ''}
                .
              </Typography>

              {isLoadingMailSenderInfo ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  {mailSenderInfo.Counts.total === 0 ? (
                    <Alert severity='warning'>
                      Es wurde kein passender Benutzer gefunden. Mit dieser
                      Einstellung wird daher immer der Standard-Absender
                      verwendet.
                    </Alert>
                  ) : (
                    <Alert
                      severity={
                        mailSenderInfo.Counts.withoutSignature === 0 &&
                        mailSenderInfo.Counts.withoutEmail === 0
                          ? 'success'
                          : 'warning'
                      }
                    >
                      {mailSenderInfo.Counts.withoutSignature === 0 &&
                      mailSenderInfo.Counts.withoutEmail === 0
                        ? `Alle ${mailSenderInfo.Counts.total} geprüften Absender haben eine E-Mail-Adresse und eine Signatur hinterlegt.`
                        : `Geprüft: ${mailSenderInfo.Counts.total} mögliche Absender. ` +
                          `Signatur hinterlegt: ${mailSenderInfo.Counts.withSignature}, ohne Signatur: ${mailSenderInfo.Counts.withoutSignature}.` +
                          (mailSenderInfo.Counts.withoutEmail > 0
                            ? ` Ohne E-Mail-Adresse: ${mailSenderInfo.Counts.withoutEmail} — diese können nicht als Absender auftreten, es greift die jeweils nächste Stufe.`
                            : ' E-Mail-Adresse ist überall hinterlegt.')}
                    </Alert>
                  )}

                  {mailSenderInfo.UserGroups.length > 0 && (
                    <>
                      <Typography
                        variant='overline'
                        color='text.secondary'
                        sx={{ display: 'block', mt: 2 }}
                      >
                        Mitarbeitergruppen (Gruppenpostfach)
                      </Typography>
                      <List dense disablePadding>
                        {mailSenderInfo.UserGroups.map(group => (
                          <SenderGroupRow
                            key={group.idUserGroup}
                            group={group}
                            onOpenGroup={openUserGroup}
                          />
                        ))}
                      </List>
                    </>
                  )}

                  {mailSenderInfo.Users.length > 0 && (
                    <>
                      {mailSenderInfo.UserGroups.length > 0 && (
                        <Typography
                          variant='overline'
                          color='text.secondary'
                          sx={{ display: 'block', mt: 2 }}
                        >
                          Einzelne Benutzer
                        </Typography>
                      )}
                      <List dense sx={{ mt: 1 }} disablePadding>
                        {mailSenderInfo.Users.map(user => (
                          <SenderUserRow
                            key={user.idUser}
                            user={user}
                            onOpenUser={openUser}
                          />
                        ))}
                      </List>
                    </>
                  )}

                  {mailSenderInfo.Counts.total > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Stack
                        direction='row'
                        spacing={2}
                        alignItems='center'
                        justifyContent='space-between'
                        flexWrap='wrap'
                      >
                        <Typography variant='caption' color='text.secondary'>
                          Signaturen werden je Benutzer bzw. je Benutzergruppe
                          im Reiter „Signatur" hinterlegt (Benutzer können ihre
                          eigene Signatur zusätzlich unter „Mein Konto"
                          pflegen).
                        </Typography>
                        <Stack direction='row' spacing={1}>
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={<People />}
                            onClick={() => navigate('/admin/user')}
                          >
                            Benutzer
                          </Button>
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={<Groups />}
                            onClick={() => navigate('/admin/groups')}
                          >
                            Benutzergruppen
                          </Button>
                        </Stack>
                      </Stack>
                    </>
                  )}
                </>
              )}
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};
